import React, { useState } from "react";
import { Paper, Text, Badge, Group, ActionIcon, Box, Button, Collapse, Pagination, Divider, Stack } from "@mantine/core";
import { IconPlus, IconChevronDown, IconChevronUp } from "@tabler/icons-react";
import { useDroppable } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import WorkflowCard from "./WorkflowCard";

import InlineTaskCreate from "./InlineTaskCreate";
import { createTask } from "../../services/workflowService";

const WorkflowColumn = ({ column, tasks, users, onTaskUpdate, onTaskClick }) => {
    const [collapsed, setCollapsed] = useState(false);
    const [activePage, setActivePage] = useState(1);

    const PAGE_SIZE = 10;

    // Sort tasks by deadline
    const sortedTasks = [...tasks].sort((a, b) => {
        // Both have due dates - sort by date ascending (sooner first)
        if (a.dueDate && b.dueDate) {
            return new Date(a.dueDate) - new Date(b.dueDate);
        }
        // Only A has due date - A comes first
        if (a.dueDate) return -1;
        // Only B has due date - B comes first
        if (b.dueDate) return 1;

        // Neither has due date - sort by position or creation date
        return (a.position - b.position) || (new Date(a.createdAt) - new Date(b.createdAt));
    });

    const totalPages = Math.ceil(sortedTasks.length / PAGE_SIZE);

    // Slice sorted tasks for current page
    const paginatedTasks = sortedTasks.slice((activePage - 1) * PAGE_SIZE, activePage * PAGE_SIZE);

    // Reset page if tasks change and current page is out of bounds
    React.useEffect(() => {
        if (activePage > totalPages && totalPages > 0) {
            setActivePage(totalPages);
        }
    }, [tasks.length, totalPages, activePage]);

    const { setNodeRef, isOver } = useDroppable({
        id: `column-${column.id}`,
        data: {
            columnId: column.id
        }
    });

    const handleCardClick = (task) => {
        if (onTaskClick) {
            onTaskClick(task);
        }
    };

    const handleCreateTask = () => {
        if (onTaskClick) {
            onTaskClick(null, column.id);
        }
    };

    const handleQuickCreate = async (title, columnId) => {
        try {
            await createTask({
                title,
                columnId,
                priority: "medium"
            });
            onTaskUpdate();
        } catch (error) {
            console.error("Error creating task:", error);
            throw error;
        }
    };

    return (
        <>
            <Paper
                ref={setNodeRef}
                p="sm"
                style={{
                    minWidth: 284,
                    maxWidth: 284,
                    backgroundColor: isOver ? "rgba(74, 94, 227, 0.04)" : "#f5f5f7",
                    display: "flex",
                    flexDirection: "column",
                    transition: "all 0.2s ease",
                    border: isOver ? "1px dashed #665bd1" : "1px solid transparent",
                    borderRadius: 7,
                    boxShadow: "none"
                }}
            >
                {/* Column Header */}
                <Group justify="space-between" mb="md">
                    <Group gap="xs">
                        <ActionIcon
                            size="xs"
                            variant="subtle"
                            onClick={() => setCollapsed(!collapsed)}
                        >
                            {collapsed ? <IconChevronDown size={14} /> : <IconChevronUp size={14} />}
                        </ActionIcon>
                        <Box
                            style={{
                                width: 12,
                                height: 12,
                                borderRadius: "50%",
                                backgroundColor: column.color
                            }}
                        />
                        <Text fw={600} size="sm">
                            {column.name}
                        </Text>
                        <Badge size="sm" variant="light" color="gray">
                            {tasks.length}
                        </Badge>
                    </Group>
                    <ActionIcon
                        size="sm"
                        variant="subtle"
                        onClick={handleCreateTask}
                    >
                        <IconPlus size={16} />
                    </ActionIcon>
                </Group>

                {/* Inline Task Creation */}
                <Collapse in={!collapsed}>
                    <Box mb="sm">
                        <InlineTaskCreate
                            columnId={column.id}
                            onTaskCreated={handleQuickCreate}
                        />
                    </Box>
                </Collapse>

                {/* Tasks List */}
                <Collapse in={!collapsed}>
                    <Box
                        style={{
                            flex: 1,
                            minHeight: 100,
                            display: "flex",
                            flexDirection: "column",
                            gap: "8px"
                        }}
                    >
                        <SortableContext
                            items={paginatedTasks.map((t) => t.id)}
                            strategy={verticalListSortingStrategy}
                        >
                            {paginatedTasks.map((task) => (
                                <WorkflowCard
                                    key={task.id}
                                    task={task}
                                    users={users}
                                    onClick={() => handleCardClick(task)}
                                    onUpdate={onTaskUpdate}
                                />
                            ))}
                        </SortableContext>

                        {tasks.length === 0 && (
                            <Text c="dimmed" size="sm" ta="center" mt="md">
                                No tasks
                            </Text>
                        )}
                    </Box>
                </Collapse>

                {/* Pagination */}
                <Collapse in={!collapsed && totalPages > 1}>
                    <Box mt="md">
                        <Divider mb="sm" variant="dotted" />
                        <Stack align="center" gap={5}>
                            <Pagination
                                value={activePage}
                                onChange={setActivePage}
                                total={totalPages}
                                size="sm"
                                siblings={0}
                                boundaries={0}
                                styles={{
                                    control: {
                                        fontSize: '10px',
                                        height: '24px',
                                        minWidth: '24px'
                                    }
                                }}
                            />
                            <Text size="xs" c="dimmed">
                                Page {activePage} of {totalPages} ({tasks.length} total)
                            </Text>
                        </Stack>
                    </Box>
                </Collapse>

                {/* Add Task Button */}
                <Button
                    variant="subtle"
                    color="brand"
                    fullWidth
                    mt="md"
                    leftSection={<IconPlus size={14} />}
                    onClick={handleCreateTask}
                >
                    Add Task
                </Button>
            </Paper>
        </>
    );
};

export default WorkflowColumn;
